import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from 'src/common/utils/api';
import type { CalendarEvent } from 'src/meetings/models';
import { meetingsQueryKey } from 'src/meetings/hooks/useScopedMeetings';
import type { MeetingScope } from 'src/meetings/scope';

type AssignSpeakerParticipant =
  | { participantType: 'CLIENT'; clientId: string }
  | { participantType: 'USER'; userId: string }
  | { participantType: 'EXTERNAL'; name: string; description: string };

// What to do when the person being assigned already speaks on this transcript. Merging shows both
// voices under the one name and cannot be picked apart afterwards; swapping has the two speakers
// exchange names, leaving both as separate voices.
export type SpeakerConflictResolution = 'merge' | 'swap';

export type AssignSpeakerBody = {
  // Every diarised voice behind one adviser-facing speaker, assigned in one request so a
  // failure part-way cannot leave that speaker split across two names.
  speakerLabels: string[];
  participant: AssignSpeakerParticipant;
  onConflict?: SpeakerConflictResolution;
};

export const useAssignSpeaker = (scope: MeetingScope, eventId: string) => {
  const queryClient = useQueryClient();

  return useMutation<{ data: CalendarEvent }, Error, AssignSpeakerBody>({
    mutationFn: body =>
      api.post<CalendarEvent>(`calendar-events/${eventId}/transcript/assign-speaker`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: meetingsQueryKey(scope) });
      queryClient.invalidateQueries({ queryKey: ['calendar-events', eventId, 'transcript'] });
      // An existing note was generated against the old speaker names, so it has just gone out of
      // date. Refetch it so the regenerate prompt appears without a reload.
      queryClient.invalidateQueries({ queryKey: ['calendar-events', eventId, 'meeting-notes'] });
    },
  });
};
